import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toDateKey } from '../../../../../utils/dateUtils';

/**
 * @typedef {Object} ConflictInitialState
 * @property {Array} conflicts - List of conflict objects.
 * @property {Array} workers - List of all workers for filtering.
 * @property {boolean} isDetectionEnabled - Whether conflict detection is active.
 * @property {Object|null} initialFocusResource - Optional resource to auto-expand on mount.
 * @property {Set<string>|null} expandedResources - Externally managed set of expanded resource GIDs.
 * @property {Function|null} onExpandedChange - Callback for changes to expandedResources.
 * @property {Array} kolonnen - List of all kolonnen (needed for multi-perspective grouping).
 */

const formatReason = (s) => {
    if (!s) return '';
    const low = s.toLowerCase();
    if (low === 'krank') return 'Krankheit';
    if (low === 'gesperrt') return 'Fahrzeug: gesperrt';
    return low.charAt(0).toUpperCase() + low.slice(1);
};

/**
 * Custom hook for managing the state and logic of the Conflict Center.
 * @param {ConflictInitialState} props 
 */
export const useConflictState = ({ 
    conflicts = [], 
    workers = [], 
    isDetectionEnabled = true, 
    initialFocusResource = null,
    kolonnen = [],
    expandedResources: externalExpanded = null,
    onExpandedChange: onExternalExpandChange = null,
    activeTab: externalActiveTab = null,
    onActiveTabChange: onExternalActiveTabChange = null
}) => {
    const [internalActiveTab, setInternalActiveTab] = useState('resources');
    const [search, setSearch] = useState('');
    
    const activeTab = externalActiveTab || internalActiveTab;
    const setActiveTab = onExternalActiveTabChange || setInternalActiveTab;
    
    // State definitions
    const [resTypeFilter, setResTypeFilter] = useState(['kolonne', 'worker', 'vehicle']);
    const [reasonFilter, setReasonFilter] = useState([]);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' }); 
    
    // Local state for expandedResources if not externally provided
    const [internalExpanded, setInternalExpanded] = useState(() => {
        const initial = new Set();
        if (initialFocusResource?.type && initialFocusResource?.id) {
            initial.add(`${initialFocusResource.type}-${initialFocusResource.id}`);
        }
        return initial;
    });

    // Use external state if available, otherwise use local state
    const expandedResources = externalExpanded || internalExpanded;
    const setExpandedResources = onExternalExpandChange || setInternalExpanded;
    
    // UI visibility states
    const [activeDropdown, setActiveDropdown] = useState(null); // 'resType', 'date', 'reason'

    const allReasons = useMemo(() => {
        const set = new Set(conflicts.map(c => formatReason(c.reason)));
        return Array.from(set).sort();
    }, [conflicts]);

    // Sync reasonFilter: Auto-include NEW reasons if they appear and the filter was "all inclusive" or empty
    useEffect(() => {
        if (allReasons.length > 0) {
            setReasonFilter(prev => {
                // Remove reasons that no longer exist in allReasons
                const validPrev = prev.filter(r => allReasons.includes(r));
                if (validPrev.length === 0) return allReasons;
                
                // Add any new reasons from allReasons that weren't in validPrev
                const newReasons = allReasons.filter(r => !validPrev.includes(r));
                if (newReasons.length > 0 || validPrev.length !== prev.length) {
                    return [...validPrev, ...newReasons];
                }
                return prev;
            });
        }
    }, [allReasons]);

    const allFilteredConflicts = useMemo(() => {
        if (!isDetectionEnabled) return [];
        return conflicts.filter(c => {
            const capReason = formatReason(c.reason);
            const matchesSearch = !search || 
                c.resourceName.toLowerCase().includes(search.toLowerCase()) ||
                capReason.toLowerCase().includes(search.toLowerCase()) ||
                c.date.includes(search);
            
            const matchesReason = reasonFilter.includes(capReason);
            // If 'kolonne' is selected, we must allow both 'worker' and 'vehicle' types 
            // into the base list so they can be grouped into teams later.
            const matchesResType = resTypeFilter.includes(c.type) || resTypeFilter.includes('kolonne');
            const matchesDateRange = (!dateRange.start || c.date >= toDateKey(dateRange.start)) &&
                                    (!dateRange.end || c.date <= toDateKey(dateRange.end));

            return matchesSearch && matchesReason && matchesResType && matchesDateRange;
        });
    }, [conflicts, search, reasonFilter, resTypeFilter, dateRange, isDetectionEnabled]);

    const todayStr = useMemo(() => toDateKey(new Date()), []);

    const filteredConflictsForTab = useMemo(() => {
        return allFilteredConflicts.filter(c => {
            const isHistorical = c.date < todayStr;
            const matchesTabScope = (activeTab === 'archive') ? isHistorical : !isHistorical;
            return matchesTabScope;
        }).sort((a, b) => {
            const { key, direction } = sortConfig;
            let valA, valB;
            if (key === 'date') [valA, valB] = [a.date, b.date];
            else if (key === 'resource') [valA, valB] = [a.resourceName, b.resourceName];
            else if (key === 'reason') [valA, valB] = [formatReason(a.reason), formatReason(b.reason)];
            return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
        });
    }, [allFilteredConflicts, activeTab, todayStr, sortConfig]);

    // --- Complex Grouping Logic ---
    const grouping = useMemo(() => {
        // Map resources to ALL kolonnen they belong to in the master list
        const resToKMap = {}; // key -> Array of kIds
        kolonnen.forEach(k => {
            (k.members || []).forEach(m => {
                const key = `${m.type}-${m.resource_id}`;
                if (!resToKMap[key]) resToKMap[key] = [];
                resToKMap[key].push(String(k.id));
            });
        });

        const handledIds = new Set();
        const sharedKeyMap = {}; // key -> { kId, items: [] }

        filteredConflictsForTab.forEach(c => {
            const involvedKIds = new Set();
            const involvedAssignments = (c.assignments || [c.assignment]).filter(Boolean);
            involvedAssignments.forEach(a => {
                const list = c.type === 'worker' ? a.workers : a.vehicles;
                const res = (list || []).find(r => String(r.worker_id || r.vehicle_id || r.id) === String(c.resourceId));
                if (res?.kolonne_id) involvedKIds.add(String(res.kolonne_id));
            });

            if (involvedKIds.size === 0) {
                const globalKIds = resToKMap[`${c.type}-${c.resourceId}`] || [];
                globalKIds.forEach(id => involvedKIds.add(id));
            }

            if (!c.reason.toLowerCase().includes('abwesenheit') && !c.absence && involvedAssignments.length > 0) {
                const aIds = involvedAssignments.map(a => String(a.id)).sort().join(',');
                involvedKIds.forEach(kId => {
                    const key = `${kId}-${c.date}-${c.reason}-${aIds}`;
                    if (!sharedKeyMap[key]) {
                        sharedKeyMap[key] = { 
                            kId, 
                            item: { ...c, assignments: involvedAssignments }, 
                            memberMatches: new Set(),
                            sourceConflicts: [] 
                        };
                    }
                    sharedKeyMap[key].memberMatches.add(`${c.type}-${c.resourceId}`);
                    sharedKeyMap[key].sourceConflicts.push(c);
                });
            }
        });

        const kGroupsMap = {};
        Object.keys(sharedKeyMap).forEach(key => {
            const entry = sharedKeyMap[key];
            if (entry.memberMatches.size > 1) { 
                if (!kGroupsMap[entry.kId]) {
                    const kolonne = kolonnen.find(k => String(k.id) === entry.kId);
                    kGroupsMap[entry.kId] = {
                        id: entry.kId,
                        type: 'kolonne',
                        name: kolonne?.name || `Team ${entry.kId}`,
                        role: kolonne?.code || '—',
                        items: []
                    };
                }
                const isDup = kGroupsMap[entry.kId].items.some(it => 
                    it.date === entry.item.date && 
                    it.reason === entry.item.reason && 
                    it.assignments.length === entry.item.assignments.length
                );
                if (!isDup) {
                    kGroupsMap[entry.kId].items.push(entry.item);
                    entry.sourceConflicts.forEach(c => {
                        handledIds.add(`${c.type}-${c.resourceId}-${c.date}-${c.reason}`);
                    });
                }
            }
        });

        // Individual groups
        const individualGroups = {};
        filteredConflictsForTab.forEach(c => {
            if (!handledIds.has(`${c.type}-${c.resourceId}-${c.date}-${c.reason}`)) {
                const gid = `${c.type}-${c.resourceId}`;
                if (!individualGroups[gid]) {
                    individualGroups[gid] = { id: c.resourceId, type: c.type, name: c.resourceName, role: c.role, items: [] };
                }
                individualGroups[gid].items.push(c);
            }
        });

        const kGroups = Object.values(kGroupsMap)
            .filter(() => resTypeFilter.includes('kolonne'))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
            
        const pGroups = Object.values(individualGroups)
            .filter(g => g.type === 'worker' && resTypeFilter.includes('worker'))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
            
        const vGroups = Object.values(individualGroups)
            .filter(g => g.type === 'vehicle' && resTypeFilter.includes('vehicle'))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

        let count = 0;
        kGroups.forEach(g => count += g.items.filter(it => it.date >= todayStr).length);
        pGroups.forEach(g => count += g.items.filter(it => it.date >= todayStr).length);
        vGroups.forEach(g => count += g.items.filter(it => it.date >= todayStr).length);

        const flat = [];
        kGroups.forEach(g => g.items.forEach(it => flat.push({ ...it, resType: 'kolonne', resName: g.name, resRole: g.role })));
        pGroups.forEach(g => g.items.forEach(it => flat.push({ ...it, resType: 'worker', resName: g.name, resRole: g.role })));
        vGroups.forEach(g => g.items.forEach(it => flat.push({ ...it, resType: 'vehicle', resName: g.name, resRole: g.role })));

        const sortedFlat = flat.sort((a, b) => {
            const { key, direction } = sortConfig;
            let valA, valB;
            if (key === 'date') [valA, valB] = [a.date, b.date];
            else if (key === 'resource') [valA, valB] = [a.resName, b.resName];
            else if (key === 'reason') [valA, valB] = [formatReason(a.reason), formatReason(b.reason)];
            else [valA, valB] = [a[key] || '', b[key] || ''];

            return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
        });

        return {
            kolonneGroups: kGroups,
            personGroups: pGroups,
            vehicleGroups: vGroups,
            allFlattened: sortedFlat,
            logicalCount: count
        };
    }, [filteredConflictsForTab, kolonnen, todayStr, sortConfig]);

    const toggleSort = (key) => setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));

    const toggleExpand = (gid) => {
        const next = new Set(expandedResources);
        if (next.has(gid)) next.delete(gid);
        else next.add(gid);
        setExpandedResources(next);
    };

    const toggleReason = (r) => {
        if (reasonFilter.includes(r)) setReasonFilter(reasonFilter.filter(item => item !== r));
        else setReasonFilter([...reasonFilter, r]);
    };

    const toggleResType = (type) => {
        if (resTypeFilter.includes(type)) setResTypeFilter(resTypeFilter.filter(item => item !== type));
        else setResTypeFilter([...resTypeFilter, type]);
    };

    const resetFilters = React.useCallback(() => {
        setSearch('');
        setResTypeFilter(['kolonne', 'worker', 'vehicle']);
        setReasonFilter(allReasons);
        setDateRange({ start: null, end: null });
        setActiveDropdown(null);
    }, [allReasons]);

    const hasActiveFilters = useMemo(() => {
        return search !== '' || 
               resTypeFilter.length !== 3 || 
               reasonFilter.length !== allReasons.length || 
               dateRange.start !== null || 
               dateRange.end !== null;
    }, [search, resTypeFilter, reasonFilter, allReasons, dateRange]);

    return {
        isDetectionEnabled,
        activeTab, setActiveTab,
        search, setSearch,
        reasonFilter, setReasonFilter,
        resTypeFilter, setResTypeFilter,
        dateRange, setDateRange,
        sortConfig, toggleSort,
        expandedResources, toggleExpand,
        setExpandedResources,
        activeDropdown, setActiveDropdown,
        allReasons,
        filteredConflicts: filteredConflictsForTab,
        logicalCount: grouping.logicalCount,
        kolonneGroups: grouping.kolonneGroups,
        personGroups: grouping.personGroups,
        vehicleGroups: grouping.vehicleGroups,
        allFlattened: grouping.allFlattened,
        todayStr,
        toggleReason,
        toggleResType,
        formatReason,
        resetFilters,
        hasActiveFilters
    };
};
